import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { PDFDocument, PDFFont, PDFPage, StandardFonts, rgb } from "pdf-lib";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, BarController, Tooltip, Legend } from "chart.js";
import { format } from "date-fns";
import { IDayStats, METRICS, sumTotals } from "./types";

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, Legend);

export interface ISummaryExport {
  title: string;
  range: string;
  metrics: { label: string; value: string | number }[];
  dayHeaders: string[];
  // Last row is the Total row
  dayRows: (string | number)[][];
  // PNG data url of the bar chart
  chartImage?: string;
}

const NAVY = "17324d";
const BLUE = "#1f5f8b";
const ORANGE = "#ed7d31";

/* Shared with the dashboard */

// Bar chart of Scheduled vs Showed Up per day
export const getChartData = (days: IDayStats[]) => ({
  labels: days.map((day) => format(day.date, "MMM d, yyyy")),
  datasets: [
    { label: "Scheduled", data: days.map((day) => day.totals.scheduled), backgroundColor: BLUE },
    { label: "Showed Up", data: days.map((day) => day.totals.showedUp), backgroundColor: ORANGE },
  ],
});

export const chartOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: "bottom" } },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, ticks: { precision: 0 }, grid: { borderDash: [4, 4] } },
  },
};

// Draws the chart on a detached canvas, for exports made without the dashboard on screen
export const renderChartImage = (days: IDayStats[]) => {
  const canvas = document.createElement("canvas");
  canvas.width = 1000;
  canvas.height = 450;
  const chart = new ChartJS(canvas, {
    type: "bar",
    data: getChartData(days),
    options: { ...chartOptions, responsive: false, animation: false, devicePixelRatio: 1 },
  });
  const image = chart.toBase64Image();
  chart.destroy();
  return image;
};

// The dashboard's metric table, daily table and chart in export form
export const buildSummaryExport = (projectName: string, range: string, days: IDayStats[], chartImage?: string): ISummaryExport => {
  const totals = sumTotals(days.map((day) => day.totals));
  return {
    title: `${projectName} | User Dashboard`,
    range,
    metrics: METRICS.map((metric) => ({ label: metric.label, value: totals[metric.key] })),
    dayHeaders: ["Date", ...METRICS.map((metric) => metric.short), "Users"],
    dayRows: [
      ...days.map((day) => [
        format(day.date, "MMM d, yyyy"),
        ...METRICS.map((metric) => day.totals[metric.key]),
        day.activeUsers.length ? day.activeUsers.join(", ") : "None",
      ]),
      ["Total", ...METRICS.map((metric) => totals[metric.key]), ""],
    ],
    chartImage,
  };
};
const navyRgb = rgb(0x17 / 255, 0x32 / 255, 0x4d / 255);
const borderRgb = rgb(0.8, 0.8, 0.8);

/* Excel */

const border = { style: "thin" as const, color: { argb: "FFBFBFBF" } };
const borders = { top: border, left: border, bottom: border, right: border };

const headerStyle = (cell: ExcelJS.Cell) => {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${NAVY}` } };
  cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
  cell.border = borders;
  cell.alignment = { vertical: "middle", wrapText: true };
};

export const saveWorkbook = async (workbook: ExcelJS.Workbook, fileName: string) => {
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), fileName);
};

// A plain table sheet: navy header row, bordered cells, wrapped text
export const addTableSheet = (
  workbook: ExcelJS.Workbook,
  name: string,
  headers: string[],
  rows: (string | number)[][],
  widths: number[]
) => {
  const sheet = workbook.addWorksheet(name);
  sheet.columns = headers.map((header, index) => ({ header, width: widths[index] || 20 }));
  sheet.getRow(1).eachCell((cell) => headerStyle(cell));
  sheet.getRow(1).height = 24;
  rows.forEach((values) => {
    const row = sheet.addRow(values);
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = borders;
      cell.alignment = { vertical: "top", wrapText: true, ...(typeof cell.value === "number" ? { horizontal: "right" as const } : {}) };
    });
  });
  return sheet;
};

export const exportSummaryExcel = async (data: ISummaryExport, fileName: string) => {
  const workbook = new ExcelJS.Workbook();
  addSummarySheet(workbook, data);
  await saveWorkbook(workbook, fileName);
};

export const addSummarySheet = (workbook: ExcelJS.Workbook, data: ISummaryExport, name: string = "Overall Summary") => {
  const sheet = workbook.addWorksheet(name);
  const columnCount = data.dayHeaders.length;

  sheet.columns = data.dayHeaders.map((header, index) => ({ width: index === 0 ? 30 : index === columnCount - 1 ? 40 : 18 }));

  // Title bar
  sheet.mergeCells(1, 1, 1, columnCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = data.title.toUpperCase();
  titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${NAVY}` } };
  sheet.getRow(1).height = 30;
  sheet.getCell(2, 1).value = data.range;

  // Metric table
  const metricStart = 4;
  ["Metric", "Value"].forEach((label, index) => {
    const cell = sheet.getCell(metricStart, index + 1);
    cell.value = label;
    headerStyle(cell);
  });
  data.metrics.forEach((metric, index) => {
    const row = sheet.getRow(metricStart + 1 + index);
    row.getCell(1).value = metric.label;
    row.getCell(2).value = metric.value;
    row.getCell(1).border = borders;
    row.getCell(2).border = borders;
    row.getCell(2).alignment = { horizontal: "right" };
  });

  // Chart to the right of the metric table, roughly 16 rows tall
  const chartRows = 16;
  if (data.chartImage) {
    const image = workbook.addImage({ base64: data.chartImage, extension: "png" });
    sheet.addImage(image, { tl: { col: 3, row: metricStart - 1 }, ext: { width: 640, height: 320 } });
  }

  // Daily table
  const dayStart = Math.max(metricStart + data.metrics.length, metricStart + (data.chartImage ? chartRows : 0)) + 2;
  const headerRow = sheet.getRow(dayStart);
  data.dayHeaders.forEach((header, index) => {
    headerRow.getCell(index + 1).value = header;
    headerStyle(headerRow.getCell(index + 1));
  });
  headerRow.height = 30;
  data.dayRows.forEach((values, rowIndex) => {
    const row = sheet.getRow(dayStart + 1 + rowIndex);
    const isTotal = rowIndex === data.dayRows.length - 1;
    values.forEach((value, index) => {
      const cell = row.getCell(index + 1);
      cell.value = value;
      cell.border = isTotal ? { ...borders, top: { style: "medium", color: { argb: `FF${NAVY}` } } } : borders;
      if (isTotal) cell.font = { bold: true };
      if (typeof value === "number") cell.alignment = { horizontal: "right" };
      if (index === values.length - 1) cell.alignment = { wrapText: true, vertical: "top" };
    });
  });

  return sheet;
};

/* PDF */

// Standard fonts only support WinAnsi characters
const clean = (text: string | number) => String(text).replace(/[^\x20-\x7E\xA0-\xFF]/g, "?");

const wrapText = (text: string, font: PDFFont, size: number, width: number) => {
  const lines: string[] = [];
  let line = "";
  clean(text)
    .split(" ")
    .forEach((word) => {
      const next = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(next, size) <= width || !line) {
        line = next;
      } else {
        lines.push(line);
        line = word;
      }
    });
  if (line) lines.push(line);
  return lines.length ? lines : [""];
};

export const exportSummaryPdf = async (data: ISummaryExport, fileName: string) => {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  // A4 landscape
  const pageWidth = 842;
  const pageHeight = 595;
  const margin = 30;
  const contentWidth = pageWidth - margin * 2;
  const size = 9;
  const padding = 4;
  const lineHeight = size + 3;

  let page: PDFPage = pdf.addPage([pageWidth, pageHeight]);
  let y = pageHeight - margin;

  const newPage = () => {
    page = pdf.addPage([pageWidth, pageHeight]);
    y = pageHeight - margin;
  };

  const drawCell = (text: string, x: number, top: number, width: number, height: number, options: { header?: boolean; bold?: boolean; align?: "right" } = {}) => {
    page.drawRectangle({
      x,
      y: top - height,
      width,
      height,
      color: options.header ? navyRgb : undefined,
      borderColor: options.header ? navyRgb : borderRgb,
      borderWidth: 0.5,
    });
    const cellFont = options.header || options.bold ? bold : font;
    wrapText(text, cellFont, size, width - padding * 2).forEach((line, index) => {
      const textWidth = cellFont.widthOfTextAtSize(line, size);
      page.drawText(line, {
        x: options.align === "right" ? x + width - padding - textWidth : x + padding,
        y: top - padding - size - index * lineHeight + 2,
        size,
        font: cellFont,
        color: options.header ? rgb(1, 1, 1) : rgb(0, 0, 0),
      });
    });
  };

  const rowHeight = (values: string[], widths: number[], rowFont: PDFFont) =>
    Math.max(...values.map((value, index) => wrapText(value, rowFont, size, widths[index] - padding * 2).length)) * lineHeight + padding * 2;

  // Title bar
  const titleHeight = 32;
  page.drawRectangle({ x: margin, y: y - titleHeight, width: contentWidth, height: titleHeight, color: navyRgb });
  page.drawText(clean(data.title.toUpperCase()), { x: margin + 10, y: y - 21, size: 15, font: bold, color: rgb(1, 1, 1) });
  const rangeWidth = font.widthOfTextAtSize(clean(data.range), 10);
  page.drawText(clean(data.range), { x: margin + contentWidth - 10 - rangeWidth, y: y - 20, size: 10, font, color: rgb(1, 1, 1) });
  y -= titleHeight + 14;

  // Metric table on the left
  const metricWidths = [170, 70];
  const metricTop = y;
  let metricY = y;
  const metricHeader = rowHeight(["Metric", "Value"], metricWidths, bold);
  drawCell("Metric", margin, metricY, metricWidths[0], metricHeader, { header: true });
  drawCell("Value", margin + metricWidths[0], metricY, metricWidths[1], metricHeader, { header: true });
  metricY -= metricHeader;
  data.metrics.forEach((metric) => {
    const height = rowHeight([metric.label, String(metric.value)], metricWidths, font);
    drawCell(metric.label, margin, metricY, metricWidths[0], height);
    drawCell(String(metric.value), margin + metricWidths[0], metricY, metricWidths[1], height, { align: "right" });
    metricY -= height;
  });

  // Chart on the right, matching the metric table height
  let chartBottom = metricY;
  if (data.chartImage) {
    const image = await pdf.embedPng(data.chartImage);
    const chartX = margin + metricWidths[0] + metricWidths[1] + 16;
    const maxWidth = margin + contentWidth - chartX;
    const maxHeight = Math.max(metricTop - metricY, 200);
    const scale = Math.min(maxWidth / image.width, maxHeight / image.height);
    const width = image.width * scale;
    const height = image.height * scale;
    page.drawRectangle({ x: chartX, y: metricTop - maxHeight, width: maxWidth, height: maxHeight, borderColor: borderRgb, borderWidth: 0.5 });
    page.drawImage(image, { x: chartX + (maxWidth - width) / 2, y: metricTop - maxHeight + (maxHeight - height) / 2, width, height });
    chartBottom = Math.min(metricY, metricTop - maxHeight);
  }
  y = chartBottom - 20;

  // Daily table: Date, metrics, Users (takes the remaining width)
  const dateWidth = 75;
  const metricWidth = 62;
  const metricCount = data.dayHeaders.length - 2;
  const dayWidths = [dateWidth, ...Array(metricCount).fill(metricWidth), contentWidth - dateWidth - metricWidth * metricCount];

  const drawDayHeader = () => {
    const height = rowHeight(data.dayHeaders, dayWidths, bold);
    let x = margin;
    data.dayHeaders.forEach((header, index) => {
      drawCell(header, x, y, dayWidths[index], height, { header: true });
      x += dayWidths[index];
    });
    y -= height;
  };

  if (y - 60 < margin) newPage();
  drawDayHeader();
  data.dayRows.forEach((values, rowIndex) => {
    const text = values.map(String);
    const isTotal = rowIndex === data.dayRows.length - 1;
    const height = rowHeight(text, dayWidths, isTotal ? bold : font);
    if (y - height < margin) {
      newPage();
      drawDayHeader();
    }
    let x = margin;
    text.forEach((value, index) => {
      drawCell(value, x, y, dayWidths[index], height, { bold: isTotal, align: typeof values[index] === "number" ? "right" : undefined });
      x += dayWidths[index];
    });
    if (isTotal) page.drawLine({ start: { x: margin, y }, end: { x: margin + contentWidth, y }, thickness: 1.5, color: navyRgb });
    y -= height;
  });

  const bytes = await pdf.save();
  saveAs(new Blob([bytes], { type: "application/pdf" }), fileName);
};
